import type { FC, ReactNode } from 'react'

import { Fragment, useEffect, useRef } from 'react'
import { BallMark } from '@/components/landing/ball-mark'
import { CountUp } from '@/components/landing/count-up'
import { Eyebrow } from '@/components/landing/eyebrow'
import { clamp, prefersReducedMotion } from '@/lib/motion'
import { addTicker } from '@/lib/ticker'
import { cn } from '@/lib/utils'
import { SHOT_WORDS, STATS } from '@/constants/event'

const STAT_BORDERS = [
    '',
    'border-l pl-4 md:pl-8',
    'border-t lg:border-l lg:border-t-0 lg:pl-8',
    'border-l border-t pl-4 md:pl-8 lg:border-t-0'
]

export const NumbersSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const marqueeRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (prefersReducedMotion()) return

        return addTicker(() => {
            const section = sectionRef.current
            const marquee = marqueeRef.current
            if (!section || !marquee) return

            const rect = section.getBoundingClientRect()
            const viewportHeight = window.innerHeight
            if (rect.bottom < 0 || rect.top > viewportHeight) return

            const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height))
            marquee.style.transform = `translate3d(${-progress * marquee.scrollWidth * 0.32}px, 0, 0)`
        })
    }, [])

    return (
        <section
            id='numbers'
            ref={sectionRef}
            aria-labelledby='numbers-title'
            className='relative overflow-hidden bg-lime text-ink'
        >
            <div className='mx-auto max-w-[1440px] px-5 pb-16 pt-24 md:px-10 md:pb-20 md:pt-32'>
                <Eyebrow>The format</Eyebrow>

                <h2 id='numbers-title' className='mt-5 max-w-5xl font-heading text-[clamp(3.2rem,9vw,8.75rem)] font-black uppercase leading-[0.84] tracking-[-0.01em]'>
                    Every rally starts
                    {' '}
                    <span className='serif-soft font-serif font-normal normal-case italic tracking-normal'>underhand.</span>
                </h2>

                <p className='mt-7 max-w-xl text-lg leading-relaxed text-ink/80'>
                    Four days in the desert, eight doubles divisions and two rules everyone learns in their first game: the serve goes underhand, and nobody volleys from the kitchen.
                </p>

                <dl className='mt-14 grid grid-cols-2 border-t border-ink/20 md:mt-20 lg:grid-cols-4'>
                    {STATS.map((stat, index) => (
                        <div key={stat.label} className={cn('flex flex-col gap-3 border-ink/20 py-7 pr-4 md:py-10', STAT_BORDERS[index])}>
                            <dt className='order-2 max-w-[15rem] text-sm leading-snug text-ink/75'>
                                {stat.label}
                            </dt>

                            <dd className='order-1 font-heading text-[clamp(3.5rem,8vw,7.5rem)] font-black leading-[0.85]'>
                                <CountUp value={stat.value} />
                                {stat.suffix && (
                                    <span className='serif-soft ml-1 font-serif text-[0.42em] font-normal italic'>
                                        {stat.suffix}
                                    </span>
                                )}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>

            <div aria-hidden='true' className='overflow-hidden border-t border-ink/15 py-8 md:py-12'>
                <div ref={marqueeRef} className='flex w-max items-center gap-[3vw] whitespace-nowrap pl-5 will-change-transform md:pl-10'>
                    {[...SHOT_WORDS, ...SHOT_WORDS].map((word, index) => (
                        <Fragment key={`${word}-${index}`}>
                            <span
                                className={cn(
                                    'font-heading text-[19vw] font-black uppercase leading-[0.8] md:text-[12vw]',
                                    index % 2 === 1 ? 'text-outline text-ink' : 'text-ink'
                                )}
                            >
                                {word}
                            </span>

                            <BallMark className='size-[7vw] md:size-[4.5vw]' />
                        </Fragment>
                    ))}
                </div>
            </div>
        </section>
    )
}
